/**
 * Record the visuals (plus the music) to a video file with MediaRecorder (OUT-7). Everything stays
 * on the device: the file is handed to the browser as a download when recording stops.
 */

/**
 * Container/codec preferences, best first. Recording runs alongside the visuals, so cheap
 * real-time encoders win: H.264 (usually hardware, plays everywhere), then VP8. VP9 software
 * encoding is too heavy to share the machine with a show. Firefox records WebM only, Safari MP4.
 */
const MIME_TYPES = [
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4;codecs=avc1,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9,opus',
  'video/webm',
  'video/mp4',
];

/** How long to wait for a final chunk that arrives after the recorder's 'stop' event. */
const LATE_DATA_MS = 1500;

export function pickMimeType(isSupported: (type: string) => boolean): string | null {
  return MIME_TYPES.find((t) => isSupported(t)) ?? null;
}

export function recordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    'captureStream' in HTMLCanvasElement.prototype &&
    pickMimeType((t) => MediaRecorder.isTypeSupported(t)) !== null
  );
}

/** "glowtrope-2026-10-05-2130.webm" */
export function recordingFileName(mime: string, now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `glowtrope-${stamp}.${mime.startsWith('video/mp4') ? 'mp4' : 'webm'}`;
}

export class Recorder {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;
  readonly mimeType: string;

  constructor() {
    const mime = pickMimeType((t) => MediaRecorder.isTypeSupported(t));
    if (!mime) throw new Error('This browser can’t record video.');
    this.mimeType = mime;
  }

  get active(): boolean {
    return this.recorder?.state === 'recording';
  }

  start(canvas: HTMLCanvasElement, audio: MediaStreamTrack | null, fps = 60): void {
    // Works on a canvas whose drawing was transferred to the render worker, too.
    const video = canvas.captureStream(fps);
    this.stream = new MediaStream([...video.getVideoTracks(), ...(audio ? [audio] : [])]);
    this.chunks = [];
    this.recorder = new MediaRecorder(this.stream, {
      mimeType: this.mimeType,
      videoBitsPerSecond: 12_000_000,
    });
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    // Chunked so a long set never holds one giant buffer inside the encoder.
    this.recorder.start(1000);
  }

  stop(): Promise<Blob> {
    const rec = this.recorder;
    if (!rec) return Promise.resolve(new Blob([], { type: this.mimeType }));
    return new Promise((resolve) => {
      const finish = () => {
        // Stop only the video track: the audio track belongs to the engine's recording tap.
        this.stream?.getVideoTracks().forEach((t) => t.stop());
        this.recorder = null;
        this.stream = null;
        resolve(new Blob(this.chunks, { type: this.mimeType }));
        this.chunks = [];
      };
      rec.onstop = () => {
        if (this.chunks.length > 0) return finish();
        // Safari can deliver the final chunk after 'stop', late when the machine is busy.
        const late = setTimeout(finish, LATE_DATA_MS);
        rec.addEventListener('dataavailable', () => {
          clearTimeout(late);
          setTimeout(finish, 0);
        });
      };
      rec.stop();
    });
  }
}
